import React, { useState } from 'react';
import { useQuery, useMutation } from 'react-query';
import { fetchRabbitMQErrorMessages, applyRabbitMQActions } from 'utils/ts-queries';
import { GroupedErrorMessages, RabbitMQAction } from 'types/rabbitMQ';
import MessageAccordion from './MessageAccordion';
import ActionButtons from './ActionButtons';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';

function AdministrationRabbitMQManager() {
  const [notification, setNotification] = useState<{ open: boolean; message: string; severity: "success" | "error" }>({
    open: false,
    message: "",
    severity: "success"
  });

  const [errorMessages, setErrorMessages] = useState<GroupedErrorMessages[]>([]);

  const { isLoading, isError, error, refetch, isFetched } = useQuery(
    "rabbitMQErrorMessages",
    fetchRabbitMQErrorMessages,
    {
      enabled: false,
      onSuccess: (data) => {
        const messagesWithAction = data.map(group => ({
          ...group,
          messages: group.messages.map(message => ({
            ...message,
            action: 'none' as RabbitMQAction
          }))
        }));
        setErrorMessages(messagesWithAction);
      }
    }
  );

  const applyMutation = useMutation(applyRabbitMQActions, {
    onSuccess: (data) => {
      // Le décompte "remplacé(s) par plus récent" n'a de sens que si une pièce avait
      // plusieurs messages sélectionnés : on ne l'affiche que dans ce cas.
      const supersededPart = data.skippedSupersededByNewer
        ? `, ${data.skippedSupersededByNewer} ignoré(s) car remplacé(s) par un message plus récent`
        : "";

      setNotification({
        open: true,
        message: `${data.requeued} rejoué(s), ${data.skippedUpToDate} ignoré(s) car déjà à jour${supersededPart}, ${data.deleted} supprimé(s), ${data.kept} conservé(s).`,
        severity: "success"
      });
      refetch();
    },
    onError: (err: any) => {
      setNotification({
        open: true,
        message: `Erreur lors de l'application des actions: ${err.message}`,
        severity: "error"
      });
    }
  });

  const handleActionChange = (groupIndex: number, messageIndexes: number[], action: RabbitMQAction) => {
    const indexes = new Set(messageIndexes);
    setErrorMessages(prev => prev.map((group, gi) => {
      if (gi !== groupIndex) return group;
      return {
        ...group,
        messages: group.messages.map((message, mi) =>
          indexes.has(mi) ? { ...message, action } : message
        )
      };
    }));
  };

  const buildActionsById = (): Record<string, Exclude<RabbitMQAction, 'none'>> => {
    const acc: Record<string, Exclude<RabbitMQAction, 'none'>> = {};
    errorMessages.forEach(group => {
      group.messages.forEach(message => {
        if (message.id && message.action && message.action !== 'none') {
          acc[message.id] = message.action;
        }
      });
    });
    return acc;
  };

  const hasPendingActions = errorMessages.some(group =>
    group.messages.some(message => message.action && message.action !== 'none')
  );

  const hasMessages = errorMessages.some(group => group.messages.length > 0);

  const handleSetAllActions = (action: RabbitMQAction) => {
    setErrorMessages(prev => prev.map(group => ({
      ...group,
      messages: group.messages.map(message => ({ ...message, action }))
    })));
  };

  const handleReadQueue = () => {
    refetch();
  };

  const handleApply = () => {
    const actionsById = buildActionsById();
    if (Object.keys(actionsById).length === 0) return;
    if (!window.confirm("Confirmer l'application des actions sélectionnées sur la file d'erreur ?")) return;
    applyMutation.mutate(actionsById);
  };

  const handleCloseNotification = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };

  return (
    <>
      <Typography gutterBottom variant="h5">
        Gestion des files RabbitMQ
      </Typography>

      <Paper>
        <Box p={2}>
          <ActionButtons
            onReadQueue={handleReadQueue}
            onApply={handleApply}
            onSetAllActions={handleSetAllActions}
            isLoading={isLoading || applyMutation.isLoading}
            hasPendingActions={hasPendingActions}
            hasMessages={hasMessages}
          />

          {isLoading || applyMutation.isLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : isError ? (
            <Alert severity="error">
              {error instanceof Error ? error.message : "Une erreur est survenue lors de la récupération des messages"}
            </Alert>
          ) : (
            <MessageAccordion
              groupedMessages={errorMessages}
              onActionChange={handleActionChange}
              showEmptyState={isFetched}
            />
          )}
        </Box>
      </Paper>

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleCloseNotification} severity={notification.severity}>
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default AdministrationRabbitMQManager;
